import { selectActiveTitle, selectActiveVersion, selectActiveVersions } from '@/redux/selectors/titleSelectors';
import { isActiveVersionAVersion, isShowUnspecified, TitleEntity } from '@/redux/slices/slice_types';
import { setActiveVersion } from '@/redux/slices/titleSlice';
import styled from '@emotion/styled';
import { Grid, Paper } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { selectCanViewInventory } from '@/redux/selectors/authSelectors';
import { selectOutputVersionOptions, selectVersionKeys } from '@/redux/selectors/orderPlacementSelectors';
import { selectDataTableFilters } from '@/redux/selectors/dataTableSelectors';
import { acceptVersionsFromTitleSlice } from '@/redux/slices/orderPlacementSlice';
import PolarisIcon from '@/components/_common/PolarisIcon';
import Tab from '@/components/_common/Tabs/Tab';
import TabHeading from '@/components/_common/Tabs/TabHeading';
import TabPanel from '@/components/_common/Tabs/TabPanel';
import Tabs from '@/components/_common/Tabs/Tabs';
import VersionSelect, { UNSPECIFIED_VERSION } from '@/components/_common/VersionSelect';
import { useQuery } from '@/components/_hooks/useQuery';
import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import TitleDetailInventory from './TitleDetailInventory/TitleDetailInventory';
import TitleDetailMetadata from './TitleDetailMetadata/TitleDetailMetadata';
import TitleDetailSynopsis from './TitleDetailMetadata/TitleDetailSynopsis';
import TitleDetailRelatedJobs from './TitleDetailRelatedJobs/TitleDetailRelatedJobs';
import TitleCustomerAssignment from './TitleCustomerAssignment';
import TitleDetailExtraMetadata from './TitleDetailMetadata/TitleDetailExtraMetadata';

const TabNames = ['metadata', 'inventory', 'related_jobs'];

const TitleDetailTabs = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const canViewInventory = useAppSelector(selectCanViewInventory);
  const titleSliceVersions = useAppSelector(selectActiveVersions);
  const outputVersionOptions = useAppSelector(selectOutputVersionOptions);
  const versionKeys = useAppSelector(selectVersionKeys);
  const activeVersion = useAppSelector(selectActiveVersion);
  const activeTitle = useAppSelector(selectActiveTitle);
  const dataTableFilters = useAppSelector(selectDataTableFilters);
  const showRelatedJobs = useContext(ConfigContext)?.titleConfig?.shouldShowRelatedJobs;
  const versionSelectShowUnspecifiedVersion = useConfigValue(['versionSelectShowUnspecifiedVersion']);

  const query = useQuery();
  const [tab, setTab] = useState(0);

  const selectedTab = query.get('tab');
  const seasonId = query.get('season_id');
  const episodeId = query.get('episode_id');
  const versionId = query.get('version_id');
  const lookupId: string = episodeId || seasonId || id;

  const versions = outputVersionOptions[lookupId] || [];
  const hasVersions = activeTitle?.children?.length === 0 || activeTitle?.children?.[0]?.entity_type === 'version' || versions.length > 0;
  const dropdownOptions = versions.map((v: TitleEntity) => String(v.id));
  // dataTableFilters?.search contains the search query from the search bar on the Titles page (if such a query was made)
  const searchedVersionId = dataTableFilters?.search
    ? versions.find((version) => [version.external_id, version.alt_external_id].includes(dataTableFilters.search))?.id
    : undefined;

  useEffect(() => {
    if (!versionId) {
      dispatch(setActiveVersion(null));
    }
  }, [versionId]);

  const handleTabChange = (_e: any, newTab: number) => {
    setTab(newTab);
    // get the current url and replace the tab param
    let url = `/titles/${id}/?tab=${TabNames[newTab]}`;
    if (seasonId) url += `&season_id=${seasonId}`;
    if (episodeId) url += `&episode_id=${episodeId}`;
    if (versionId) url += `&version_id=${versionId}`;
    history.push(url);
  };

  useEffect(() => {
    if (!versions?.length) return;

    if (query.get('version_id') === 'all') {
      dispatch(setActiveVersion({}));
      return;
    }

    const queryVersionId = query.get('version_id');
    const firstVersionId = versions[0].id;

    if (!queryVersionId) {
      selectVersion({ target: { value: searchedVersionId || firstVersionId } });
      return;
    }

    if (queryVersionId === UNSPECIFIED_VERSION) {
      selectVersion({ target: { value: UNSPECIFIED_VERSION } });
    } else if (!versions.find((v) => String(v.id) === queryVersionId)) {
      selectVersion({ target: { value: firstVersionId } });
    }
  }, [versions, searchedVersionId]);

  useEffect(() => {
    if (selectedTab) {
      setTab(TabNames.indexOf(selectedTab));
    }
  }, [selectedTab]);

  useEffect(() => {
    if (!lookupId) return;
    dispatch(acceptVersionsFromTitleSlice({ versions: titleSliceVersions, id: lookupId }));
  }, [lookupId, titleSliceVersions]);

  useEffect(() => {
    if (!versionId || !versionKeys || !versionKeys[versionId]) return;
    dispatch(setActiveVersion(versionKeys[versionId]));
  }, [versionId, versionKeys]);

  const selectVersion = (e: any) => {
    const versionId = e.target.value;
    let url = `/titles/${id}/?tab=${TabNames[tab]}`;
    if (seasonId) url += `&season_id=${seasonId}`;
    if (episodeId) url += `&episode_id=${episodeId}`;
    if (versionId === UNSPECIFIED_VERSION && versionSelectShowUnspecifiedVersion) {
      url += `&showUnspecified=true&version_id=${versionId}`;
      dispatch(setActiveVersion({ showUnspecified: true }));
    } else if (!versionId) {
      url += `&version_id=all`;
      dispatch(setActiveVersion({}));
    } else {
      url += `&version_id=${versionId}`;
    }
    history.push(url);
  };

  return (
    <Grid item sx={{ mt: 5, position: 'relative' }}>
      {/* VERSIONS DROPDOWN */}
      <Grid
        container
        item
        justifyContent="flex-end"
        alignItems="center"
        sx={{ position: 'relative', minHeight: 54, ml: 'auto', maxWidth: 500, mb: -5, zIndex: 999 }}
      >
        {hasVersions && (
          <VersionSelect
            showUnspecifiedVersionOption={versionSelectShowUnspecifiedVersion}
            label={!activeVersion || versionId === 'all' ? 'All' : 'Version'}
            value={isShowUnspecified(activeVersion) ? UNSPECIFIED_VERSION : isActiveVersionAVersion(activeVersion) ? activeVersion.id : null}
            options={dropdownOptions}
            onChange={selectVersion}
            disableClearable={false}
            forceEnableOptions
            sx={{ width: 300 }}
            highContrast
          />
        )}
        <TitleCustomerAssignment />
      </Grid>

      <Tabs value={tab} onChange={handleTabChange} aria-label="title detail tabs">
        <Tab label="Metadata" />
        {canViewInventory && <Tab label="Inventory" />}
        {showRelatedJobs && <Tab label="Related Jobs" />}
      </Tabs>

      <StyledTabPanel value={tab} index={0} style={{ borderTopLeftRadius: 0 }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <TabHeading icon={<PolarisIcon icon="info" />}>Metadata</TabHeading>
        </Grid>

        <TitleDetailMetadata />

        {isActiveVersionAVersion(activeVersion) && <TitleDetailExtraMetadata />}

        {activeTitle?.synopsis && <TitleDetailSynopsis />}
      </StyledTabPanel>

      <TabPanel value={tab} index={1}>
        <StyledPaper sx={{ p: 3, pt: 4 }}>
          <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <TabHeading icon={<PolarisIcon icon="files" />}>Inventory</TabHeading>
          </Grid>
          {tab === 1 && canViewInventory && (
            <TitleDetailInventory
              showUnspecified={versionSelectShowUnspecifiedVersion && isShowUnspecified(activeVersion)}
              selectedVersion={isActiveVersionAVersion(activeVersion) ? activeVersion.name : undefined}
            />
          )}
        </StyledPaper>
      </TabPanel>

      {showRelatedJobs && (
        <StyledTabPanel value={tab} index={2}>
          <Grid container justifyContent="space-between" alignItems="center">
            <TabHeading icon={<PolarisIcon icon="related_jobs" />}>Related Jobs</TabHeading>
          </Grid>
          {tab === 2 && <TitleDetailRelatedJobs lookupId={lookupId as string} />}
        </StyledTabPanel>
      )}
    </Grid>
  );
};

export default TitleDetailTabs;

const StyledTabPanel = styled(TabPanel)`
  background: ${(props) => props.theme.palette.background.paper};
  padding: ${(props) => props.theme.spacing(4)};
  border-radius: ${(props) => props.theme.shape.borderRadius};
  box-shadow: 0px 4px 18px rgba(40, 41, 61, 0.05);
`;

const StyledPaper = styled(Paper)`
  border: 0px;
  box-shadow: none !important;
  border-radius: 0px;
`;
